// ST-32 -Claude- Companies list and Nearrme Admin company onboarding wizard.
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { createCompany, listCompanies } from '../api';
import { imageSpecs } from '../imageSpecs';
import { nextSort, SortHeader, sortRows, SortState } from '../tableSort';
import { ImageUploadField } from '../components/ImageUploadField';

const STEPS = ['Company', 'Users', 'Products', 'Businesses'] as const;

type Step = (typeof STEPS)[number];

function generateCompanyCode(name: string) {
  const compact = name.replace(/[^a-z0-9]/gi, '').toUpperCase();
  return (compact + 'NEARRME').slice(0, 7);
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'company';
}

export function AdminCompaniesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortState<'company' | 'url' | 'type' | 'contact' | 'users' | 'status'>>({
    key: 'company',
    direction: 'asc',
  });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState<Step>('Company');
  const [form, setForm] = useState({
    companyName: '',
    legalName: '',
    email: '',
    phoneNumber: '',
    address: '',
    websiteUrl: '',
    publicUrlDecorator: '',
    businessType: 'Manufacturer',
    logoUrl: '',
    notes: '',
    assignedUserEmails: '',
    initialProducts: '',
    initialStores: '',
    isActive: true,
  });

  const generatedCode = useMemo(() => generateCompanyCode(form.companyName), [form.companyName]);
  const publicSlug = form.publicUrlDecorator || slugify(form.companyName);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: listCompanies,
  });

  const createMutation = useMutation({
    mutationFn: () => createCompany({
      companyCode: generatedCode,
      companyName: form.companyName,
      legalName: form.legalName,
      email: form.email,
      phoneNumber: form.phoneNumber,
      address: form.address,
      websiteUrl: form.websiteUrl,
      publicUrlDecorator: publicSlug,
      businessType: form.businessType,
      logoUrl: form.logoUrl,
      notes: form.notes,
      isActive: form.isActive,
      assignedUserEmails: form.assignedUserEmails.split(',').map((x) => x.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      setWizardOpen(false);
      setStep('Company');
      setForm({
        companyName: '',
        legalName: '',
        email: '',
        phoneNumber: '',
        address: '',
        websiteUrl: '',
        publicUrlDecorator: '',
        businessType: 'Manufacturer',
        logoUrl: '',
        notes: '',
        assignedUserEmails: '',
        initialProducts: '',
        initialStores: '',
        isActive: true,
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
    },
  });

  const currentStepIndex = STEPS.indexOf(step);
  const sortedCompanies = useMemo(
    () => sortRows(data ?? [], sort, {
      company: (c) => c.companyName ?? c.companyCode,
      url: (c) => c.publicUrlDecorator ?? c.companyName,
      type: (c) => c.businessType,
      contact: (c) => c.email ?? c.phoneNumber,
      users: (c) => c.assignedUserEmails?.length ?? 0,
      status: (c) => c.isActive ? 1 : 0,
    }),
    [data, sort]
  );

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Companies</h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            Manage company profiles, public landing page identity, teams, products, stores, and orders.
          </p>
        </div>
        <Button variant="primary" onClick={() => setWizardOpen((value) => !value)}>
          {wizardOpen ? 'Close wizard' : 'Add company'}
        </Button>
      </div>

      {wizardOpen && (
        <Card padded className="mt-4 border border-slate-200 shadow-sm dark:border-slate-800">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-50">Company onboarding wizard</h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Create the company first, then attach users, products, and businesses/stores.
              </p>
            </div>
            <div className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              Code <span className="font-mono font-semibold">{generatedCode || '-------'}</span> | URL <span className="font-mono">nearrme.com/{publicSlug}</span>
            </div>
          </div>

          <div className="mt-5 grid gap-2 sm:grid-cols-4">
            {STEPS.map((item, index) => (
              <button
                key={item}
                type="button"
                onClick={() => setStep(item)}
                className={`rounded-md border px-3 py-2 text-left text-sm font-semibold ${
                  item === step
                    ? 'border-brand-600 bg-brand-50 text-brand-800 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-200'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-900'
                }`}
              >
                <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Step {index + 1}</span>
                {item}
              </button>
            ))}
          </div>

          {step === 'Company' && (
            <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Display name" value={form.companyName} onChange={(value) => setForm({ ...form, companyName: value, publicUrlDecorator: form.publicUrlDecorator || slugify(value) })} />
                <Field label="Legal name" value={form.legalName} onChange={(value) => setForm({ ...form, legalName: value })} />
                <SelectField label="Business type" value={form.businessType} onChange={(value) => setForm({ ...form, businessType: value })} />
                <Field label="Public URL decorator" value={form.publicUrlDecorator} placeholder={slugify(form.companyName)} onChange={(value) => setForm({ ...form, publicUrlDecorator: slugify(value) })} />
                <Field label="Email" type="email" value={form.email} onChange={(value) => setForm({ ...form, email: value })} />
                <Field label="Phone" value={form.phoneNumber} onChange={(value) => setForm({ ...form, phoneNumber: value })} />
                <Field label="Website" value={form.websiteUrl} onChange={(value) => setForm({ ...form, websiteUrl: value })} />
                <ImageUploadField label="Company logo" value={form.logoUrl} onChange={(value) => setForm({ ...form, logoUrl: value })} spec={imageSpecs.companyLogo} />
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Landing page preview</div>
                <div className="mt-3 text-xl font-bold text-slate-950 dark:text-slate-50">{form.companyName || 'Company name'}</div>
                <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">nearrme.com/{publicSlug}</div>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                  This page will list products grouped by brand, open deals, stores, and manufacturer verification.
                </p>
              </div>
              <Field label="Short address" value={form.address} placeholder="City, state, country" onChange={(value) => setForm({ ...form, address: value })} />
              <Field label="Internal notes" value={form.notes} onChange={(value) => setForm({ ...form, notes: value })} />
            </div>
          )}

          {step === 'Users' && (
            <WizardPanel
              title="Add users"
              body="Add owner, admin, and manager emails now. Roles and permissions remain controlled by Nearrme Admin."
            >
              <Field
                label="Initial users"
                value={form.assignedUserEmails}
                placeholder="owner@company.com, manager@company.com"
                onChange={(value) => setForm({ ...form, assignedUserEmails: value })}
              />
            </WizardPanel>
          )}

          {step === 'Products' && (
            <WizardPanel
              title="Add products"
              body="Capture starting product groups for onboarding. Product import and brand grouping will be handled from the company profile."
            >
              <TextArea
                label="Initial products or categories"
                value={form.initialProducts}
                placeholder="Brand A: Product 1, Product 2"
                onChange={(value) => setForm({ ...form, initialProducts: value })}
              />
            </WizardPanel>
          )}

          {step === 'Businesses' && (
            <WizardPanel
              title="Add businesses and stores"
              body="Add the first store or business locations that will appear on the company landing page and Nearrme search."
            >
              <TextArea
                label="Initial stores"
                value={form.initialStores}
                placeholder="Store name, city, phone"
                onChange={(value) => setForm({ ...form, initialStores: value })}
              />
            </WizardPanel>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
            <Button variant="secondary" disabled={currentStepIndex === 0} onClick={() => setStep(STEPS[currentStepIndex - 1])}>
              Back
            </Button>
            {currentStepIndex < STEPS.length - 1 ? (
              <Button variant="primary" onClick={() => setStep(STEPS[currentStepIndex + 1])}>
                Continue
              </Button>
            ) : (
              <Button variant="primary" disabled={!form.companyName || createMutation.isPending} onClick={() => createMutation.mutate()}>
                Create company
              </Button>
            )}
            {createMutation.error && (
              <span className="text-sm text-red-600 dark:text-red-400">{(createMutation.error as Error).message}</span>
            )}
          </div>
        </Card>
      )}

      {isLoading && (
        <div className="mt-6 space-y-2">
          {[1, 2, 3].map((i) => <LoadingSkeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {error && (
        <Card padded className="mt-6">
          <div className="text-sm text-red-600 dark:text-red-400">{(error as Error).message}</div>
        </Card>
      )}

      {data && (
        <>
          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {data.length} {data.length === 1 ? 'company' : 'companies'}
          </div>
          {data.length === 0 ? (
            <EmptyState title="No companies" description="The API returned an empty list." />
          ) : (
            <Card padded={false} className="mt-3 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Company" sortKey="company" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Public URL" sortKey="url" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Type" sortKey="type" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Contact" sortKey="contact" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Users" sortKey="users" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300"><SortHeader label="Status" sortKey="status" sort={sort} onSort={(key) => setSort((current) => nextSort(current, key))} /></th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {sortedCompanies.map((c) => (
                      <tr
                        key={c.companyId ?? c.companyCode}
                        onClick={() => navigate(`/admin/companies/${encodeURIComponent(c.companyCode ?? '')}`)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            navigate(`/admin/companies/${encodeURIComponent(c.companyCode ?? '')}`);
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:hover:bg-slate-900 dark:focus:bg-slate-900"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 dark:text-slate-100">{c.companyName ?? '-'}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            <span className="font-mono">{c.companyCode}</span>
                            {c.legalName ? ` | ${c.legalName}` : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">
                          nearrme.com/{c.publicUrlDecorator ?? slugify(c.companyName ?? c.companyCode ?? '')}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{c.businessType ?? '-'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          <div>{c.email ?? '-'}</div>
                          <div className="text-xs text-slate-500">{c.phoneNumber ?? '-'}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{c.assignedUserEmails?.join(', ') || '-'}</td>
                        <td className="px-4 py-3">
                          {c.isActive ? (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-950 dark:text-green-400">Active</span>
                          ) : (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-400">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Link to={`/admin/companies/${encodeURIComponent(c.companyCode ?? '')}`} className="text-sm font-semibold text-brand-700 hover:underline dark:text-brand-300">
                            Open
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function WizardPanel(props: { title: string; body: string; children: ReactNode }) {
  return (
    <div className="mt-5 rounded-lg border border-slate-200 p-4 dark:border-slate-800">
      <h3 className="text-base font-semibold text-slate-950 dark:text-slate-50">{props.title}</h3>
      <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">{props.body}</p>
      <div className="mt-4">{props.children}</div>
    </div>
  );
}

function Field(props: { label: string; value: string; onChange: (value: string) => void; type?: string; placeholder?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">{props.label}</span>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(event) => props.onChange(event.target.value)}
        className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      />
    </label>
  );
}

function SelectField(props: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">{props.label}</span>
      <select
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      >
        <option>Manufacturer</option>
        <option>Distributor</option>
        <option>Dealer</option>
        <option>Retailer</option>
        <option>Other</option>
      </select>
    </label>
  );
}

function TextArea(props: { label: string; value: string; onChange: (value: string) => void; placeholder?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">{props.label}</span>
      <textarea
        value={props.value}
        placeholder={props.placeholder}
        onChange={(event) => props.onChange(event.target.value)}
        className="min-h-28 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      />
    </label>
  );
}
